import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';

interface Capture {
  id: string;
  content: string;
  source: string;
  is_processed: boolean;
  created_at: string;
}

interface QuickCaptureProps {
  onCaptureCreated?: () => void;
}

// Check if Web Speech API is available
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
const speechAvailable = !!SpeechRecognition;

const QuickCapture: React.FC<QuickCaptureProps> = ({ onCaptureCreated }) => {
  const [captures, setCaptures] = useState<Capture[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    if (isExpanded) {
      fetchCaptures();
    }
  }, [isExpanded]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (speechAvailable && !recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0].transcript)
          .join('');
        setInput(transcript);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setSpeechError('Microphone access denied. Please allow microphone permission.');
        } else if (event.error === 'no-speech') {
          setSpeechError('No speech detected. Try again.');
        } else {
          setSpeechError(`Error: ${event.error}`);
        }
        setTimeout(() => setSpeechError(null), 3000);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const toggleListening = () => {
    if (!speechAvailable) {
      setSpeechError('Voice input not supported in this browser');
      setTimeout(() => setSpeechError(null), 3000);
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      setSpeechError(null);
      try {
        recognitionRef.current?.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
        setSpeechError('Failed to start voice input');
        setTimeout(() => setSpeechError(null), 3000);
      }
    }
  };

  const fetchCaptures = async () => {
    try {
      const response = await apiService.getQuickCaptures();
      if (response.success) {
        setCaptures(response.captures || []);
      }
    } catch (error) {
      console.error('Failed to fetch captures:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    // Stop listening if active
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    }

    setLoading(true);
    try {
      // Track source - if we were listening recently, mark as voice
      const source = isListening ? 'voice' : 'text';
      const response = await apiService.createQuickCapture(input.trim(), source);
      if (response.success) {
        setInput('');
        setCaptures([response.capture, ...captures]);
        if (onCaptureCreated) onCaptureCreated();
      }
    } catch (error) {
      console.error('Failed to create capture:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async (capture: Capture) => {
    try {
      const response = await apiService.convertCaptureToTask(capture.id);
      if (response.success) {
        setCaptures(captures.filter(c => c.id !== capture.id));
        if (onCaptureCreated) onCaptureCreated();
      }
    } catch (error) {
      console.error('Failed to convert capture:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.deleteQuickCapture(id);
      setCaptures(captures.filter(c => c.id !== id));
    } catch (error) {
      console.error('Failed to delete capture:', error);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 1000
    }}>
      {/* Expanded Panel */}
      {isExpanded && (
        <div style={{
          width: '350px',
          maxHeight: '500px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          marginBottom: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '16px 20px',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Quick Capture</h3>
              <span style={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                padding: '2px 8px', 
                borderRadius: '12px',
                fontSize: '0.75rem'
              }}>
                {captures.length} ideas
              </span>
            </div>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.7rem', opacity: 0.9 }}>
              Jot down ideas quickly - convert to tasks when ready
            </p>
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
            {/* Speech Error Message */}
            {speechError && (
              <div style={{
                marginBottom: '8px',
                padding: '8px 12px',
                backgroundColor: '#fef2f2',
                color: '#991b1b',
                borderRadius: '8px',
                fontSize: '0.75rem'
              }}>
                {speechError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={isListening ? "Listening..." : "Capture an idea..."}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: `2px solid ${isListening ? '#ef4444' : '#e5e7eb'}`,
                  borderRadius: '10px',
                  fontSize: '0.875rem',
                  outline: 'none',
                  backgroundColor: isListening ? '#fef2f2' : 'white'
                }}
                onFocus={(e) => { if (!isListening) e.target.style.borderColor = '#667eea'; }}
                onBlur={(e) => { if (!isListening) e.target.style.borderColor = '#e5e7eb'; }}
              />
              {/* Microphone Button */}
              <button
                type="button"
                onClick={toggleListening}
                data-testid="voice-capture-btn"
                style={{
                  padding: '10px 14px',
                  backgroundColor: isListening ? '#ef4444' : (speechAvailable ? '#f3f4f6' : '#e5e7eb'),
                  color: isListening ? 'white' : (speechAvailable ? '#374151' : '#9ca3af'),
                  border: 'none',
                  borderRadius: '10px',
                  cursor: speechAvailable ? 'pointer' : 'not-allowed',
                  fontSize: '1.1rem',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                title={speechAvailable ? (isListening ? 'Stop listening' : 'Voice input') : 'Voice not supported'}
              >
                🎤
                {isListening && (
                  <span style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '10px',
                    height: '10px',
                    backgroundColor: '#ef4444',
                    borderRadius: '50%',
                    animation: 'pulse 1s infinite'
                  }} />
                )}
              </button>
              <button
                type="submit"
                disabled={!input.trim() || loading}
                style={{
                  padding: '10px 16px',
                  backgroundColor: input.trim() ? '#667eea' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  cursor: input.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                +
              </button>
            </div>
            <style>{`
              @keyframes pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.5; transform: scale(1.2); }
              }
            `}</style>
          </form>

          {/* Captures List */}
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            padding: '12px',
            maxHeight: '300px'
          }}>
            {captures.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>
                <p style={{ margin: 0 }}>No captures yet.</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.75rem' }}>Start typing to capture your ideas!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {captures.map(capture => (
                  <div
                    key={capture.id}
                    style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '10px',
                      borderLeft: '3px solid #667eea'
                    }}
                  >
                    <p style={{ margin: '0 0 8px 0', fontSize: '0.875rem', color: '#374151' }}>
                      {capture.content}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                        {formatTime(capture.created_at)}
                      </span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => handleConvert(capture)}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          → Task
                        </button>
                        <button
                          onClick={() => handleDelete(capture.id)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#fee2e2',
                            color: '#991b1b',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.7rem',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => {
          setIsExpanded(!isExpanded);
          if (!isExpanded) {
            setTimeout(() => inputRef.current?.focus(), 100);
          }
        }}
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          transition: 'transform 0.2s, box-shadow 0.2s',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
        }}
      >
        {isExpanded ? '×' : '💡'}
        {captures.length > 0 && !isExpanded && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            backgroundColor: '#ef4444',
            color: 'white',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            fontSize: '0.7rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {captures.length}
          </span>
        )}
      </button>
    </div>
  );
};

export default QuickCapture;
